
import { useEffect, useState } from "react";
import { Calendar, Eye, Users } from "lucide-react";
import { Link } from "react-router-dom";

import DashboardLayout from "../../components/DashboardLayout";
import { mastersApi, reportsApi } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/data";
import { useToast } from "@/context/ToastContext";
import { QuickStatsCards, EventFilters, EventTypeBadge, StatusBadge, type EventTotals } from "./components";
import {
  ExportButtons, ReportBody, ReportSkeleton, useCanSeeMoney, useReport, type ExportColumn, type ExportSpec,
} from "../ReportKit";

// ─── Types ───────────────────────────────────────────────────────────────────

interface EventRow {
  id: number;
  name: string;
  event_type: string | null;
  status: string;
  client: { id: number; name: string } | null;
  venue: { id: number; name: string } | null;
  package: { id: number; name: string } | null;
  start_date: string | null;
  end_date: string | null;
  guest_count: number;
  vendor_count: number;
  service_count: number;
  received: number;
  balance: number;
  payment_state: "paid" | "partial" | "unpaid" | "overdue" | null;
  revenue?: number;
  gst?: number;
  billed?: number;
  cost?: number;
  profit?: number;
  margin?: number;
  is_projected?: boolean;
}

interface EventsReport {
  can_view_money: boolean;
  events: { data: EventRow[]; current_page: number; last_page: number; per_page: number; total: number };
  totals: EventTotals;
}

type Option = { id: number; name: string };

const SORTS: { value: string; label: string; money?: boolean }[] = [
  { value: "newest", label: "Newest added" },
  { value: "date_desc", label: "Event date (latest first)" },
  { value: "date_asc", label: "Event date (earliest first)" },
  { value: "name", label: "Name (A–Z)" },
  { value: "balance", label: "Still owed (highest)" },
  { value: "revenue", label: "Revenue (highest)", money: true },
  { value: "profit", label: "Profit (lowest first)", money: true },
];

const PAY_LABEL: Record<string, string> = { paid: "Paid", partial: "Partly paid", unpaid: "Unpaid", overdue: "Overdue" };
const PAY_CLASS: Record<string, string> = {
  paid: "bg-emerald-50 text-emerald-700",
  partial: "bg-amber-100 text-amber-800",
  unpaid: "bg-stone-100 text-stone-600",
  overdue: "bg-red-50 text-red-700",
};

const asList = (res: any): Option[] => {
  const d = res?.data;
  const list = Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
  return list.map((x: any) => ({ id: x.id, name: x.name }));
};

const dateText = (r: EventRow) =>
  !r.start_date ? "—" : r.end_date && r.end_date !== r.start_date
    ? `${formatDate(r.start_date)} – ${formatDate(r.end_date)}`
    : formatDate(r.start_date);

const profitClass = (n: number) => (n >= 0 ? "text-emerald-700" : "text-red-600");

type SummaryItem = NonNullable<ExportSpec["summary"]>[number];

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft", confirmed: "Confirmed", in_progress: "In Progress", completed: "Completed", cancelled: "Cancelled",
};

/** Excel / Print: the totals cards, then one row per event (money columns only for owner and admins). */
function eventsExport(report: EventsReport, showMoney: boolean, opts: { period?: { from: string; to: string }; filters: string }): ExportSpec {
  const t = report.totals;
  const booked = (t.by_status.confirmed ?? 0) + (t.by_status.in_progress ?? 0) + (t.by_status.completed ?? 0);
  const drafts = t.by_status.draft ?? 0;
  const cancelled = t.by_status.cancelled ?? 0;
  const profit = t.profit ?? 0;

  const columns: ExportColumn[] = [
    { label: "Event" },
    { label: "Type" },
    { label: "Client" },
    { label: "Venue" },
    { label: "Start", type: "date" },
    { label: "End", type: "date" },
    { label: "Status" },
    { label: "Guests", type: "number" },
    ...(showMoney ? [
      { label: "Revenue (before GST)", type: "money" },
      { label: "GST", type: "money" },
      { label: "Billed (incl. GST)", type: "money" },
      { label: "Cost", type: "money" },
      { label: "Profit", type: "money" },
      { label: "Margin", type: "percent" },
      { label: "Projected" },
    ] satisfies ExportColumn[] : []),
    { label: "Received", type: "money" },
    { label: "Still owed", type: "money" },
    { label: "Payment" },
  ];

  const rows = report.events.data.map((e) => {
    const isCancelled = e.status === "cancelled"; // the page shows no profit for these
    return [
      e.name, e.event_type ?? "", e.client?.name ?? "", e.venue?.name ?? "",
      e.start_date, e.end_date, STATUS_LABEL[e.status] ?? e.status, e.guest_count,
      ...(showMoney ? [
        e.revenue ?? 0, e.gst ?? 0, e.billed ?? 0, e.cost ?? 0,
        isCancelled ? null : e.profit ?? 0,
        isCancelled ? null : e.margin ?? 0,
        !isCancelled && e.is_projected ? "Yes" : "",
      ] : []),
      e.received, Math.max(0, e.balance), e.payment_state ? PAY_LABEL[e.payment_state] : "",
    ];
  });

  const note = showMoney
    ? "Dates filter by the day an event starts. Revenue is after discount, before GST. Revenue − Cost = Profit. Cancelled events count as ₹0. Projected = no vendors assigned yet, so cost and profit are estimates."
    : "Dates filter by the day an event starts. Cancelled events owe nothing.";

  return {
    title: "Event Reports",
    fileName: "events-report",
    period: opts.period,
    summary: [
      { label: "Events", value: t.events, type: "number" },
      { label: "Booked", value: booked, type: "number" },
      ...(drafts ? [{ label: "Draft", value: drafts, type: "number" }] satisfies SummaryItem[] : []),
      ...(cancelled ? [{ label: "Cancelled", value: cancelled, type: "number" }] satisfies SummaryItem[] : []),
      ...(showMoney ? [
        { label: "Revenue (before GST)", value: t.revenue ?? 0, type: "money" },
        { label: "GST", value: t.gst ?? 0, type: "money" },
        { label: "Billed (incl. GST)", value: t.billed ?? 0, type: "money" },
        { label: "Cost", value: t.cost ?? 0, type: "money" },
        { label: profit >= 0 ? "Profit" : "Loss", value: Math.abs(profit), type: "money" },
        { label: "Margin", value: t.margin ?? 0, type: "percent" },
      ] satisfies SummaryItem[] : [
        { label: "Guests (not counting cancelled)", value: t.guests, type: "number" },
      ] satisfies SummaryItem[]),
      { label: "Received", value: t.received, type: "money" },
      { label: "Still owed", value: t.owed, type: "money" },
      { label: "Events with a balance", value: t.events_owing, type: "number" },
    ],
    tables: [{ title: "Events", columns, rows }],
    notes: [note, ...(opts.filters ? [opts.filters] : [])],
  };
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function EventsReportPage() {
  const canSeeMoney = useCanSeeMoney();
  const { showToast } = useToast();

  // Filter inputs (what the form shows) and the applied values (what was loaded)
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({ from: "", to: "" });
  const [appliedRange, setAppliedRange] = useState<{ from: string; to: string }>({ from: "", to: "" });
  const [statusFilter, setStatusFilter] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [venueFilter, setVenueFilter] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [eventTypes, setEventTypes] = useState<Option[]>([]);
  const [venues, setVenues] = useState<Option[]>([]);

  // Filter options; archived venues stay listed so old events can still be found
  useEffect(() => {
    Promise.all([mastersApi.eventTypes.list(), mastersApi.venues.list({ includeArchived: true })])
      .then(([typesRes, venuesRes]) => {
        setEventTypes(asList(typesRes));
        setVenues(asList(venuesRes));
      })
      .catch(() => { /* the filters still work without the option lists */ });
  }, []);

  // A date range applies once both ends are picked, or when it is cleared
  useEffect(() => {
    const complete = (dateRange.from && dateRange.to) || (!dateRange.from && !dateRange.to);
    if (complete && (dateRange.from !== appliedRange.from || dateRange.to !== appliedRange.to)) {
      setAppliedRange(dateRange);
      setPage(1);
    }
  }, [dateRange, appliedRange]);

  const params = {
    from_date: appliedRange.from,
    to_date: appliedRange.to,
    status: statusFilter,
    event_type: eventTypeFilter,
    venue_id: venueFilter,
    search: appliedSearch,
    sort,
  };

  const state = useReport<EventsReport>(
    async () => (await reportsApi.eventsReport({ ...params, page, per_page: perPage })).data,
    [appliedRange.from, appliedRange.to, statusFilter, eventTypeFilter, venueFilter, appliedSearch, sort, page, perPage],
  );

  const resetPage = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };

  // Excel / Print: every event matching the current filters (up to 1,000), not just this page
  const buildExport = async (): Promise<ExportSpec> => {
    const res = await reportsApi.eventsReport({ ...params, page: 1, per_page: 1000 });
    const report: EventsReport = res.data;
    if (report.events.total > report.events.data.length) {
      showToast(`Only the first ${report.events.data.length} of ${report.events.total} events are included`, "info");
    }
    const filters = [
      statusFilter && `Status: ${STATUS_LABEL[statusFilter] ?? statusFilter}`,
      eventTypeFilter && `Type: ${eventTypeFilter}`,
      venueFilter && `Venue: ${venues.find((v) => String(v.id) === venueFilter)?.name ?? venueFilter}`,
      appliedSearch && `Search: “${appliedSearch}”`,
    ].filter(Boolean);
    return eventsExport(report, canSeeMoney && report.can_view_money, {
      period: appliedRange.from && appliedRange.to ? appliedRange : undefined,
      filters: filters.length ? `Filters: ${filters.join(" · ")}` : "",
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            <h1 className="text-lg font-bold text-stone-800">Event Reports</h1>
            {state.data && <span className="text-sm text-stone-500">({state.data.events.total} events)</span>}
          </div>
          {state.status !== 403 && (
            <ExportButtons
              build={buildExport}
              disabled={state.loading || !state.data || state.data.events.total === 0}
            />
          )}
        </div>

        <EventFilters
          search={search}
          setSearch={setSearch}
          dateRange={dateRange}
          setDateRange={setDateRange}
          statusFilter={statusFilter}
          setStatusFilter={resetPage(setStatusFilter)}
          eventTypeFilter={eventTypeFilter}
          setEventTypeFilter={resetPage(setEventTypeFilter)}
          venueFilter={venueFilter}
          setVenueFilter={resetPage(setVenueFilter)}
          eventTypes={eventTypes}
          venues={venues}
          onApply={() => { setAppliedSearch(search.trim()); setPage(1); }}
        />

        <ReportBody state={state} skeleton={<ReportSkeleton cards={4} blocks={1} />}>
          {(d) => (
            <EventsView
              d={d}
              showMoney={canSeeMoney && d.can_view_money}
              sort={sort}
              setSort={resetPage(setSort)}
              perPage={perPage}
              setPerPage={resetPage(setPerPage)}
              setPage={setPage}
            />
          )}
        </ReportBody>
      </div>
    </DashboardLayout>
  );
}

// ─── Report body ─────────────────────────────────────────────────────────────

function EventsView({ d, showMoney, sort, setSort, perPage, setPerPage, setPage }: {
  d: EventsReport;
  showMoney: boolean;
  sort: string;
  setSort: (v: string) => void;
  perPage: number;
  setPerPage: (v: number) => void;
  setPage: (v: number) => void;
}) {
  const { data: rows, current_page: page, last_page: lastPage, total } = d.events;
  const first = total === 0 ? 0 : (page - 1) * d.events.per_page + 1;
  const last = Math.min(page * d.events.per_page, total);

  return (
    <>
      <QuickStatsCards totals={d.totals} showMoney={showMoney} />

      <section className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-200 bg-stone-50 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-xs text-stone-500">
            Sort
            <select value={sort} onChange={(e) => setSort(e.target.value)}
              className="h-10 px-2 text-sm border border-stone-300 rounded-lg bg-white text-stone-700">
              {SORTS.filter((s) => !s.money || showMoney).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
          <p className="text-xs text-stone-500">
            Dates filter by the day an event starts
            {showMoney ? " · Revenue is after discount, before GST · Revenue − Cost = Profit · cancelled events count as ₹0" : " · cancelled events owe nothing"}
          </p>
        </div>

        {rows.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-2" />
            <p className="text-sm text-stone-500">No events match these filters.</p>
          </div>
        ) : (
          <>
            {/* Phones: cards */}
            <ul className="md:hidden divide-y divide-stone-100">
              {rows.map((e) => (
                <li key={e.id}>
                  <Link to={`/reports/events/detail?id=${e.id}`} className="block p-4 active:bg-stone-50">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-stone-800 truncate">{e.name}</p>
                        <p className="text-xs text-stone-500 truncate">{e.client?.name ?? "No client"} · {dateText(e)}</p>
                      </div>
                      <StatusBadge status={e.status} />
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                      {showMoney ? (
                        <div>
                          <p className="text-stone-400">Revenue</p>
                          <p className="font-semibold text-stone-800 tabular-nums">{formatCurrency(e.revenue ?? 0)}</p>
                          {e.status !== "cancelled" && <p className={`tabular-nums ${profitClass(e.profit ?? 0)}`}>Profit {formatCurrency(e.profit ?? 0)}</p>}
                        </div>
                      ) : (
                        <div>
                          <p className="text-stone-400">Guests</p>
                          <p className="font-semibold text-stone-800">{e.guest_count || "—"}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-stone-400">Received</p>
                        <p className="font-semibold text-emerald-700 tabular-nums">{formatCurrency(e.received)}</p>
                      </div>
                      <div>
                        <p className="text-stone-400">Still owed</p>
                        <p className={`font-semibold tabular-nums ${e.balance > 0 ? "text-amber-700" : "text-stone-400"}`}>{e.balance > 0 ? formatCurrency(e.balance) : "—"}</p>
                        {e.payment_state && <span className={`inline-block mt-0.5 px-1.5 rounded text-[10px] ${PAY_CLASS[e.payment_state]}`}>{PAY_LABEL[e.payment_state]}</span>}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            {/* Tablets and up: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                    <th className="text-left px-4 py-3">Event &amp; client</th>
                    <th className="text-left px-3 py-3">Date</th>
                    <th className="text-left px-3 py-3 hidden lg:table-cell">Venue</th>
                    {showMoney && <th className="text-right px-3 py-3">Revenue</th>}
                    {showMoney && <th className="text-right px-3 py-3">Profit</th>}
                    <th className="text-right px-3 py-3">Received</th>
                    <th className="text-right px-3 py-3">Still owed</th>
                    <th className="text-center px-3 py-3">Status</th>
                    <th className="px-3 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((e) => (
                    <tr key={e.id} className="hover:bg-blue-50/40">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-stone-800">{e.name}</p>
                        <p className="text-xs text-stone-500">{e.client?.name ?? "No client"}</p>
                        <div className="mt-1"><EventTypeBadge type={e.event_type ?? undefined} /></div>
                      </td>
                      <td className="px-3 py-3 text-stone-700 whitespace-nowrap">{dateText(e)}</td>
                      <td className="px-3 py-3 hidden lg:table-cell">
                        <p className="text-stone-700">{e.venue?.name ?? "—"}</p>
                        {e.guest_count > 0 && <p className="text-xs text-stone-500 inline-flex items-center gap-1"><Users className="w-3 h-3" />{e.guest_count}</p>}
                      </td>
                      {showMoney && (
                        <td className="px-3 py-3 text-right tabular-nums whitespace-nowrap">
                          <p className="font-semibold text-stone-800">{formatCurrency(e.revenue ?? 0)}</p>
                          {(e.gst ?? 0) > 0 && <p className="text-[11px] text-stone-400">+ GST {formatCurrency(e.gst ?? 0)}</p>}
                        </td>
                      )}
                      {showMoney && (
                        <td className="px-3 py-3 text-right tabular-nums whitespace-nowrap">
                          {e.status === "cancelled" ? <span className="text-stone-400">—</span> : (
                            <>
                              <p className={`font-semibold ${profitClass(e.profit ?? 0)}`}>{formatCurrency(e.profit ?? 0)}</p>
                              <p className="text-[11px] text-stone-400">{(e.margin ?? 0).toFixed(1)}%{e.is_projected ? " · projected" : ""}</p>
                            </>
                          )}
                        </td>
                      )}
                      <td className="px-3 py-3 text-right tabular-nums text-emerald-700 whitespace-nowrap">{formatCurrency(e.received)}</td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <p className={`tabular-nums font-medium ${e.balance > 0 ? "text-amber-700" : "text-stone-400"}`}>{e.balance > 0 ? formatCurrency(e.balance) : "—"}</p>
                        {e.payment_state && <span className={`inline-block px-1.5 rounded text-[10px] ${PAY_CLASS[e.payment_state]}`}>{PAY_LABEL[e.payment_state]}</span>}
                      </td>
                      <td className="px-3 py-3 text-center"><StatusBadge status={e.status} /></td>
                      <td className="px-3 py-3 text-right">
                        <Link to={`/reports/events/detail?id=${e.id}`}
                          className="inline-flex items-center gap-1.5 h-9 px-2.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg">
                          <Eye className="w-3.5 h-3.5" /> View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="px-4 py-3 border-t border-stone-200 bg-stone-50 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-xs text-stone-600">
            <span>{first}–{last} of <strong>{total}</strong></span>
            <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} aria-label="Rows per page"
              className="h-9 text-xs border border-stone-300 rounded-lg px-2 bg-white">
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} per page</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(page - 1)} disabled={page <= 1}
              className="h-10 px-3 text-sm font-medium text-stone-600 bg-white border border-stone-300 rounded-lg disabled:opacity-40">
              ← Prev
            </button>
            <span className="text-sm text-stone-700 px-1">{page} / {lastPage}</span>
            <button onClick={() => setPage(page + 1)} disabled={page >= lastPage}
              className="h-10 px-3 text-sm font-medium text-stone-600 bg-white border border-stone-300 rounded-lg disabled:opacity-40">
              Next →
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
